
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function AddListing() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'category' | 'details' | 'images' | 'pricing' | 'promotion'>('category');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [transactionType, setTransactionType] = useState<string>('');
  const [listingData, setListingData] = useState({
    title: '',
    description: '',
    price: '',
    location: '',
    area: '',
    bedrooms: '',
    bathrooms: '',
    features: [] as string[]
  });
  const [promotionPlan, setPromotionPlan] = useState<string>('');

  const categories = [
    { id: 'real-estate', name: 'العقارات', icon: 'ri-home-line' },
    { id: 'cars', name: 'السيارات', icon: 'ri-car-line' },
    { id: 'ships', name: 'السفن', icon: 'ri-ship-line' },
    { id: 'jewelry', name: 'المجوهرات', icon: 'ri-gem-line' },
    { id: 'electronics', name: 'الإلكترونيات', icon: 'ri-smartphone-line' }
  ];

  const realEstateSubcategories = [
    { id: 'villa', name: 'الفلل السكنية' },
    { id: 'apartment', name: 'الشقق السكنية' },
    { id: 'land', name: 'الأراضي' },
    { id: 'building', name: 'العمارات' },
    { id: 'farm', name: 'المزارع' }
  ];

  const transactionTypes = [
    { id: 'sale', name: 'للبيع', icon: 'ri-price-tag-3-line', color: 'bg-green-500' },
    { id: 'rent', name: 'للإيجار', icon: 'ri-calendar-line', color: 'bg-blue-500' },
    { id: 'wanted', name: 'مطلوب', icon: 'ri-search-line', color: 'bg-orange-500' },
    { id: 'exchange', name: 'للبدل', icon: 'ri-exchange-line', color: 'bg-purple-500' }
  ];

  const promotionPlans = [
    {
      id: 'basic',
      name: 'عادي',
      price: 'مجاني',
      duration: '',
      features: ['عرض عادي', 'مدة 30 يوم', 'بدون تمييز'],
      color: 'bg-gray-600'
    },
    {
      id: 'weekly',
      name: 'مميز أسبوعي',
      price: '50',
      duration: 'أسبوع',
      features: ['ظهور في المقدمة', 'شارة مميز', 'إشعارات أكثر', 'إحصائيات مفصلة'],
      color: 'bg-blue-500'
    },
    {
      id: 'monthly',
      name: 'مميز شهري',
      price: '150',
      duration: 'شهر',
      features: ['أولوية في البحث', 'شارة ذهبية', 'دعم مخصص', 'تقارير شاملة'],
      color: 'bg-yellow-500',
      popular: true
    },
    {
      id: 'yearly',
      name: 'مميز سنوي',
      price: '1200',
      duration: 'سنة',
      features: ['أعلى أولوية', 'شارة بلاتينية', 'مدير حساب', 'تحليلات متقدمة'],
      color: 'bg-purple-500',
      discount: '33% خصم'
    }
  ];

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    if (categoryId === 'real-estate') {
      // Stay on category step to show subcategories
    } else {
      setStep('details');
    }
  };

  const handleSubcategorySelect = (subcategoryId: string) => {
    setSelectedSubcategory(subcategoryId);
    setStep('details');
  };

  const handleDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('images');
  };

  const handleImagesNext = () => {
    setStep('pricing');
  };

  const handlePricingNext = () => {
    setStep('promotion');
  };

  const handlePublish = () => {
    // Here you would submit the listing data
    alert('تم نشر الإعلان بنجاح!');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: 'Cairo, sans-serif' }}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-black/90 backdrop-blur-sm z-50 px-4 py-4">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-900"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
          <h1 className="text-lg font-semibold">إضافة إعلان</h1>
          <div className="w-10"></div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
            <span>الفئة</span>
            <span>التفاصيل</span>
            <span>الصور</span>
            <span>السعر</span>
            <span>التمييز</span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-1">
            <div 
              className="bg-yellow-400 h-1 rounded-full transition-all duration-300"
              style={{ 
                width: step === 'category' ? '20%' : 
                       step === 'details' ? '40%' : 
                       step === 'images' ? '60%' : 
                       step === 'pricing' ? '80%' : '100%' 
              }}
            ></div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-6 px-4">
        {/* Category Selection */}
        {step === 'category' && (
          <div>
            <h2 className="text-xl font-bold mb-2">اختر الفئة</h2>
            <p className="text-gray-400 mb-6">حدد فئة الإعلان الذي تريد نشره</p>
            
            <div className="space-y-3 mb-6">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategorySelect(category.id)}
                  className={`w-full bg-gray-900 rounded-xl p-4 flex items-center hover:bg-gray-800 transition-colors ${
                    selectedCategory === category.id ? 'ring-2 ring-yellow-400' : ''
                  }`}
                >
                  <div className="w-12 h-12 bg-yellow-400/10 rounded-lg flex items-center justify-center ml-4">
                    <i className={`${category.icon} text-2xl text-yellow-400`}></i>
                  </div>
                  <span className="font-medium">{category.name}</span>
                  <i className="ri-arrow-left-s-line text-gray-400 mr-auto"></i>
                </button>
              ))}
            </div>

            {/* Real Estate Subcategories */}
            {selectedCategory === 'real-estate' && (
              <div>
                <h3 className="text-lg font-semibold mb-4">نوع العقار</h3>
                <div className="space-y-3">
                  {realEstateSubcategories.map((subcategory) => (
                    <button
                      key={subcategory.id}
                      onClick={() => handleSubcategorySelect(subcategory.id)}
                      className="w-full bg-gray-800 rounded-xl p-4 flex items-center justify-between hover:bg-gray-700 transition-colors"
                    >
                      <span>{subcategory.name}</span>
                      <i className="ri-arrow-left-s-line text-gray-400"></i>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Transaction Type */}
            {(selectedCategory && selectedCategory !== 'real-estate') || selectedSubcategory ? (
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4">نوع المعاملة</h3>
                <div className="grid grid-cols-2 gap-3">
                  {transactionTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => {
                        setTransactionType(type.id);
                        setStep('details');
                      }}
                      className={`bg-gray-900 rounded-xl p-4 text-center hover:bg-gray-800 transition-colors ${
                        transactionType === type.id ? 'ring-2 ring-yellow-400' : ''
                      }`}
                    >
                      <div className={`w-10 h-10 mx-auto mb-2 rounded-lg ${type.color} flex items-center justify-center`}>
                        <i className={`${type.icon} text-white text-lg`}></i>
                      </div>
                      <span className="text-sm font-medium">{type.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* Details Form */}
        {step === 'details' && (
          <form onSubmit={handleDetailsSubmit}>
            <h2 className="text-xl font-bold mb-6">تفاصيل الإعلان</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">عنوان الإعلان</label>
                <input
                  type="text"
                  value={listingData.title}
                  onChange={(e) => setListingData({...listingData, title: e.target.value})}
                  placeholder="أدخل عنوان جذاب للإعلان"
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 focus:outline-none focus:border-yellow-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">الوصف</label>
                <textarea
                  value={listingData.description}
                  onChange={(e) => setListingData({...listingData, description: e.target.value})}
                  placeholder="اكتب وصف مفصل للإعلان..."
                  rows={4}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 focus:outline-none focus:border-yellow-500 resize-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">الموقع</label>
                <input
                  type="text"
                  value={listingData.location}
                  onChange={(e) => setListingData({...listingData, location: e.target.value})}
                  placeholder="المدينة، المنطقة"
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 focus:outline-none focus:border-yellow-500"
                  required
                />
              </div>

              {selectedCategory === 'real-estate' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2">المساحة</label>
                    <input
                      type="text"
                      value={listingData.area}
                      onChange={(e) => setListingData({...listingData, area: e.target.value})}
                      placeholder="متر مربع"
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 focus:outline-none focus:border-yellow-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">عدد الغرف</label>
                      <input
                        type="number"
                        value={listingData.bedrooms}
                        onChange={(e) => setListingData({...listingData, bedrooms: e.target.value})}
                        placeholder="0"
                        className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 focus:outline-none focus:border-yellow-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">عدد الحمامات</label>
                      <input
                        type="number"
                        value={listingData.bathrooms}
                        onChange={(e) => setListingData({...listingData, bathrooms: e.target.value})}
                        placeholder="0"
                        className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 focus:outline-none focus:border-yellow-500"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <button
              type="submit"
              className="w-full bg-yellow-400 text-black font-semibold py-3 rounded-xl mt-6"
            >
              التالي
            </button>
          </form>
        )}

        {/* Images Upload */}
        {step === 'images' && (
          <div>
            <h2 className="text-xl font-bold mb-2">إضافة الصور</h2>
            <p className="text-gray-400 mb-6">أضف صور عالية الجودة لإعلانك</p>
            
            <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-800 rounded-full flex items-center justify-center">
                <i className="ri-image-add-line text-2xl text-gray-400"></i>
              </div>
              <h3 className="font-semibold mb-2">اختر الصور</h3>
              <p className="text-sm text-gray-400 mb-4">PNG, JPG حتى 10MB</p>
              <button className="bg-yellow-400 text-black px-6 py-2 rounded-full font-medium">
                تصفح الملفات
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-square bg-gray-900 rounded-xl flex items-center justify-center">
                  <i className="ri-image-line text-2xl text-gray-600"></i>
                </div>
              ))}
            </div>

            <button
              onClick={handleImagesNext}
              className="w-full bg-yellow-400 text-black font-semibold py-3 rounded-xl"
            >
              التالي
            </button>
          </div>
        )}

        {/* Pricing */}
        {step === 'pricing' && (
          <div>
            <h2 className="text-xl font-bold mb-6">تحديد السعر</h2>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {transactionType === 'rent' ? 'الإيجار الشهري' : 'السعر'}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={listingData.price}
                    onChange={(e) => setListingData({...listingData, price: e.target.value})}
                    placeholder="0"
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 pl-16 focus:outline-none focus:border-yellow-500"
                    required
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    ر.ق
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-xl p-4">
                <h3 className="font-semibold mb-3">نصائح للتسعير</h3>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-400 ml-2 mt-0.5"></i>
                    ابحث عن أسعار مماثلة في السوق
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-400 ml-2 mt-0.5"></i>
                    ضع سعر تنافسي لجذب المزيد من المشترين
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-green-400 ml-2 mt-0.5"></i>
                    يمكنك تعديل السعر لاحقاً
                  </li>
                </ul>
              </div>
            </div>

            <button
              onClick={handlePricingNext}
              className="w-full bg-yellow-400 text-black font-semibold py-3 rounded-xl"
            >
              التالي
            </button>
          </div>
        )}

        {/* Promotion Plans */}
        {step === 'promotion' && (
          <div>
            <h2 className="text-xl font-bold mb-2">تمييز الإعلان</h2>
            <p className="text-gray-400 mb-6">اختر خطة التمييز لزيادة ظهور إعلانك</p>
            
            <div className="space-y-4 mb-6">
              {promotionPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative bg-gray-900 rounded-xl p-4 border-2 transition-colors cursor-pointer ${
                    promotionPlan === plan.id 
                      ? 'border-yellow-400 bg-yellow-400/5' 
                      : 'border-transparent hover:border-gray-700'
                  }`}
                  onClick={() => setPromotionPlan(plan.id)}
                >
                  {plan.popular && (
                    <div className="absolute -top-2 right-4 bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-semibold">
                      الأكثر شعبية
                    </div>
                  )}
                  {plan.discount && (
                    <div className="absolute -top-2 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {plan.discount}
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full border-2 ml-3 ${
                        promotionPlan === plan.id 
                          ? 'border-yellow-400 bg-yellow-400' 
                          : 'border-gray-600'
                      }`}>
                        {promotionPlan === plan.id && (
                          <div className="w-2 h-2 bg-black rounded-full m-0.5"></div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold">{plan.name}</h3>
                        <p className="text-sm text-gray-400">
                          {plan.duration && `لمدة ${plan.duration}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-left">
                      <div className="text-xl font-bold text-yellow-400">
                        {plan.price === 'مجاني' ? plan.price : `${plan.price} ر.ق`}
                      </div>
                    </div>
                  </div>
                  
                  <ul className="space-y-1">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-400">
                        <i className="ri-check-line text-green-400 ml-2"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="bg-gray-900 rounded-xl p-4 mb-6">
              <h3 className="font-semibold mb-2">ملخص الإعلان</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">الفئة:</span>
                  <span>{categories.find(c => c.id === selectedCategory)?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">العنوان:</span>
                  <span>{listingData.title || 'غير محدد'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">السعر:</span>
                  <span>{listingData.price ? `${listingData.price} ر.ق` : 'غير محدد'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">خطة التمييز:</span>
                  <span>{promotionPlans.find(p => p.id === promotionPlan)?.name || 'عادي'}</span>
                </div>
              </div>
            </div>

            <button
              onClick={handlePublish}
              className="w-full bg-yellow-400 text-black font-semibold py-3 rounded-xl"
            >
              نشر الإعلان
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
